import { cva } from "cva";
import Image from "next/image";
import { useAnalytics } from "~/components/Analytics";
import { BuildHTML } from "~/components/BuildHtml/BuildHtml";
import { CTALink } from "~/landing-pages/components/CTA/CTALink/CTALink";
import { primaryButtonStyle } from "~/landing-pages/components/CTA/cta-shared-styles";
import { sanitizeRelativeUrl } from "~/lib/shared/sanitizeRelativeUrl";
import { tagToClassNameMap } from "~/landing-pages/utils/tagToClassNameMap";
import { Body, Headline } from "~/ui-library";

export interface CTASplitWithImageProps {
  blockIndex: number;
  appearance: "brand" | "dark" | "light";
  imageUrl: string;
  imageAlt: string;
  eyebrow: string;
  headline: string;
  description: string;
  primaryCtaLink: string;
  primaryCtaText: string;
}

const blockStyle = cva(["relative"], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      brand: "bg-brand-indigo",
      dark: "bg-gray-900",
      light: "bg-white",
    },
  },
});

const eyebrowStyle = cva(["font-bold"], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      brand: "text-white",
      dark: "text-brand-indigo-300",
      light: "text-brand-indigo",
    },
  },
});

const headlineStyle = cva(["mt-2"], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      brand: "text-white",
      dark: "text-white",
      light: "text-gray-900",
    },
  },
});

const descriptionStyle = cva(["mt-6"], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      brand: "text-brand-indigo-200",
      dark: "text-gray-300",
      light: "text-gray-700",
    },
  },
});

export const CTASplitWithImage = ({
  blockIndex,
  imageUrl,
  imageAlt,
  headline,
  eyebrow,
  appearance,
  description,
  primaryCtaLink,
  primaryCtaText,
}: CTASplitWithImageProps): JSX.Element => {
  const { analyticsEnhancedTrack } = useAnalytics();
  const primaryButtonClick = () =>
    analyticsEnhancedTrack("Clicked Primary CTA", {
      blockIndex,
      appearance,
      linkText: primaryCtaText,
      linkUrl: primaryCtaLink,
    });
  return (
    <div className={blockStyle({ appearance })}>
      <div className="relative h-80 overflow-hidden bg-brand-indigo-600 md:absolute md:left-0 md:h-full md:w-1/3 lg:w-1/2">
        <Image
          className="size-full object-cover"
          width={300}
          height={320}
          src={imageUrl}
          alt={imageAlt}
        />
      </div>
      <div className="relative mx-auto max-w-7xl py-4 sm:py-6 lg:p-8">
        <div className="px-6 md:ml-auto md:w-2/3 md:pl-16 lg:w-1/2 lg:pl-24 lg:pr-0 xl:pl-32">
          <Headline
            as="div"
            level={5}
            className={eyebrowStyle({
              appearance,
            })}
          >
            {eyebrow}
          </Headline>
          <Headline level={1} as="h2" className={headlineStyle({ appearance })}>
            {headline}
          </Headline>
          <Body className={descriptionStyle({ appearance })}>
            <BuildHTML
              html={description}
              tagToClassNameMap={tagToClassNameMap}
            />
          </Body>
          <div className="mt-8">
            <CTALink
              href={sanitizeRelativeUrl(primaryCtaLink)}
              onClick={primaryButtonClick}
              className={primaryButtonStyle({ appearance })}
            >
              {primaryCtaText}
            </CTALink>
          </div>
        </div>
      </div>
    </div>
  );
};
